'use client';

import { createRoot } from 'react-dom/client';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import Dialog from '@/components/ui/Dialog';
import { motion } from 'framer-motion';

export type ConfirmationDialogVariant = 'danger' | 'primary' | 'secondary';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  variant?: ConfirmationDialogVariant;
}

const getVariantStyles = (variant: ConfirmationDialogVariant) => {
  switch (variant) {
    case 'danger':
      return 'bg-[#ef4444] hover:bg-[#dc2626] text-white';
    case 'primary':
      return 'bg-primary hover:bg-[#003C77] text-white';
    case 'secondary':
      return 'bg-gray-500 hover:bg-gray-600 text-white';
    default:
      return 'bg-[#ef4444] hover:bg-[#dc2626] text-white';
  }
};

export default function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Тийм',
  cancelText,
  isLoading = false,
  variant = 'danger',
}: ConfirmationDialogProps) {
  return (
    <Dialog
      closeOnClickOutside={false}
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      maxWidth="md"
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.1 }}
      >
        <p className="text-sm text-gray-600 mb-4">{message}</p>

        <div className="flex justify-end gap-2">
          {cancelText && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              {cancelText ?? 'Үгүй'}
            </motion.button>
          )}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${getVariantStyles(variant)}`}
          >
            {isLoading ? (
              <Loader2 className="animate-spin" color="#fff" />
            ) : (
              confirmText
            )}
          </motion.button>
        </div>
      </motion.div>
    </Dialog>
  );
}

export interface ConfirmOptions {
  title: string;
  message: string;
  onConfirm?: () => void | Promise<void>;
  variant?: ConfirmationDialogVariant;
  confirmText?: string;
  cancelText?: string;
}

export function confirmDial({
  title,
  message,
  onConfirm,
  variant = 'primary',
  confirmText,
  cancelText,
}: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    const ConfirmWrapper = () => {
      const [loading, setLoading] = useState(false);

      const handleConfirm = async () => {
        setLoading(true);
        try {
          if (onConfirm) await onConfirm();
          cleanup();
          resolve(true);
        } catch (err) {
          console.error(err);
          setLoading(false); // алдаа гарсан бол loader-оо зогсооно
        }
      };

      const handleCancel = () => {
        cleanup();
        resolve(false);
      };

      const cleanup = () => {
        root.unmount();
        container.remove();
      };

      return (
        <ConfirmationDialog
          isOpen={true}
          onClose={handleCancel}
          onConfirm={handleConfirm}
          title={title}
          message={message}
          isLoading={loading}
          variant={variant}
          confirmText={confirmText}
          cancelText={cancelText}
        />
      );
    };

    const root = createRoot(container);
    root.render(<ConfirmWrapper />);
  });
}
